import { ChevronLeftIcon } from "lucide-react";
import { match } from "ts-pattern";

import { DrawerCloseIcon, DrawerHeader, DrawerTitle } from "@/web/components/primitives/drawer";
import { CompSwipeReview } from "./CompSwipeReview";
import { IntroReview } from "./IntroReview";
import { PriceEstimateReview } from "./PriceEstimateReview";
import { TopThreeSelectionReview } from "./TopThreeSelectionReview";
import type { CompsReviewFlow } from "./useCompsReviewFlow";

export function CompsReviewPanel({
  flow,
  onClose,
}: {
  flow: CompsReviewFlow;
  onClose: () => void;
}) {
  const {
    stage,
    comps,
    propertyAddress,
    targetAddress,
    currentComp,
    remainingCount,
    interestedComps,
    topThreeCompIds,
    priceEstimate,
    priceEstimateFeedback,
    property,
    underwritableEntity,
    exitingCompId,
    setPriceEstimateFeedback,
    resetCompsSession,
    startSwipeReview,
    handleSwipe,
    handleExitComplete,
    handleToggleTopThree,
    handleComplete,
    isSubmittingTopThree,
  } = flow;

  return (
    <>
      <ReviewDrawerHeader
        showBack={stage.current === "topThree" || stage.current === "estimate"}
        onBack={stage.current === "estimate" ? stage.pop : resetCompsSession}
        onClose={onClose}
      />
      {match(stage.current)
        .with("swipe", () => (
          <CompSwipeReview
            comp={currentComp}
            exitingCompId={exitingCompId}
            remainingCount={remainingCount}
            onSwipeLeft={() => handleSwipe("left")}
            onSwipeRight={() => handleSwipe("right")}
            onExitComplete={handleExitComplete}
          />
        ))
        .with("topThree", () => (
          <TopThreeSelectionReview
            comps={interestedComps}
            targetAddress={propertyAddress}
            selectedCompIds={topThreeCompIds}
            onToggleComp={handleToggleTopThree}
            onContinue={() => stage.push("estimate")}
          />
        ))
        .with("estimate", () => (
          <PriceEstimateReview
            propertyId={property.id}
            underwritableEntity={underwritableEntity}
            priceEstimate={priceEstimate}
            selectedFeedback={priceEstimateFeedback}
            onSelectFeedback={(option) => {
              setPriceEstimateFeedback((value) => (value === option ? null : option));
            }}
            onComplete={handleComplete}
            isSubmitting={isSubmittingTopThree}
            hasAppraisal={property.orderedAppraisal ?? false}
          />
        ))
        .with("intro", () => (
          <IntroReview
            comps={comps}
            targetAddress={targetAddress}
            propertyAddress={propertyAddress}
            onStart={startSwipeReview}
          />
        ))
        .exhaustive()}
    </>
  );
}

function ReviewDrawerHeader({
  showBack,
  onBack,
  onClose,
}: {
  showBack: boolean;
  onBack: () => void;
  onClose: () => void;
}) {
  return (
    <DrawerHeader className="shrink-0">
      {showBack ? (
        <button
          type="button"
          aria-label="Back"
          className="inline-flex size-5 items-center justify-center text-withus-dark-blue"
          onClick={onBack}
        >
          <ChevronLeftIcon className="size-4" />
        </button>
      ) : (
        <span className="size-5" aria-hidden />
      )}
      <DrawerTitle>Review comparable properties</DrawerTitle>
      <DrawerCloseIcon onClick={onClose} />
    </DrawerHeader>
  );
}
